import React, {useContext} from 'react'

import {noop} from 'es-toolkit'

import './input-field.scss'

import useUniqueId from '../../hooks/use-unique-id'
import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

import {FieldLabelContext} from '../field-label/FieldLabel'
import Icon from '../icon'

type Props = Overwrite<
  CommonProps<'div'>,
  {
    inputRef?: React.RefObject<HTMLInputElement | null>
    id?: string
    name?: string
    value: string
    type?: 'text' | 'password' | 'email' | 'tel'
    size?: '90' | '100' | '200'
    appearance?: 'base' | 'error'
    placeholder?: string
    icon?: React.ReactElement<React.ComponentProps<typeof Icon>>
    prefix?: string
    suffix?: string
    action?: React.ReactElement
    autoComplete?: string
    maxLength?: number
    mixedLabel?: string
    mixed?: boolean
    disabled?: boolean
    autoFocus?: boolean
    onChange?: (
      value: string,
      name?: string | undefined,
      event?: React.ChangeEvent<HTMLInputElement>
    ) => void
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  }
>

const InputField = ({
  id,
  className,
  name,
  type = 'text',
  size = '100',
  appearance = 'base',
  placeholder,
  icon,
  value,
  prefix,
  suffix,
  action,
  autoComplete,
  maxLength,
  mixedLabel,
  mixed = false,
  disabled = false,
  autoFocus = false,
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  inputRef,
  ...props
}: Props) => {
  const innerInputRef = React.useRef<HTMLInputElement>(null)
  const contextId = useContext(FieldLabelContext).id
  const fieldId = useUniqueId(id ?? contextId)

  const handleContainerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (
      innerInputRef.current &&
      !(event.target as HTMLElement).closest('a') &&
      !(event.target as HTMLElement).closest('button')
    ) {
      innerInputRef.current.focus()
    }
  }

  React.useImperativeHandle(inputRef, () => innerInputRef.current!)

  return (
    <div
      {...props}
      className={cn(
        'InputField',
        {
          [`size-${size}`]: !!size,
          [`appearance-${appearance}`]: !!appearance,
          disabled,
          mixed,
        },
        className
      )}
      onClick={handleContainerClick}
    >
      {prefix && <div className="InputField__prefix">{prefix}</div>}

      <div className="InputField__main">
        {icon && <div className="InputField__icon">{icon}</div>}
        <input
          className="InputField__input"
          name={name}
          id={fieldId}
          type={type}
          placeholder={placeholder}
          ref={innerInputRef}
          autoFocus={autoFocus}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={event => onChange(event.target.value, name, event)}
          value={mixed ? mixedLabel : value}
          disabled={disabled || mixed}
          maxLength={maxLength}
          autoComplete={autoComplete}
        />
        {suffix && <div className="InputField__suffix">{suffix}</div>}
      </div>
      {action && <div className="InputField__action">{action}</div>}
    </div>
  )
}

export default InputField
